import React, { useCallback, useEffect, useMemo } from 'react'
import { CircularProgress } from '@material-ui/core'
import TextField from '@material-ui/core/TextField'
import Autocomplete, {
  AutocompleteRenderOptionState,
} from '@material-ui/lab/Autocomplete'
import { useTranslate } from 'ra-core'
import { assertUnreachable } from '@obvious.tech/util/function'
import { useImmer } from 'use-immer'

type TOptionTArg = string | Record<string, unknown>

export type TMatchInputCb<Option extends TOptionTArg> = (
  input: string,
  options: Option[],
) => TMatchInputResult<Option>

type TAutocompleteInputProps<
  Option extends TOptionTArg,
  FreeSolo extends boolean = false,
> = {
  input: string
  selectedOption?: Option | null
  freeSolo?: FreeSolo
  onChange: (
    option: (true extends FreeSolo ? string : never) | Option | null,
  ) => void
  onValidationError?: (error: string | undefined) => void
  onBlur?: React.FocusEventHandler<HTMLDivElement>
  optionEq: (optionA: Option, optionB: Option) => boolean
  invalid?: boolean
  options: Option[]
  loading: boolean
  optionsLoading?: boolean
  label: string
  disabled?: boolean
  onInputChange: (value: string) => void
  placeholder: string
  getOptionLabel?: (option: Option) => string
  matchInput?: TMatchInputCb<Option>
  renderOption?: (
    option: Option,
    state: AutocompleteRenderOptionState,
  ) => React.ReactNode
  getOptionDisabled?: (option: Option) => boolean
  error: unknown
  touched?: boolean
  className?: string
  required?: boolean
}

type TMatchInputResult<Option extends TOptionTArg> =
  | { _tag: 'empty'; option?: never; input?: never }
  | { _tag: 'match'; option: Option; input?: never }
  | { _tag: 'no-match'; option?: never; input: string }
  | {
      _tag: 'multiple-matches'
      option?: never
      options: Option[]
      input?: never
    }

type TInputOrigin =
  | 'user' // user typed in the field
  | 'selection' // user selected an option from the list
  | 'programmatic' // any other event (ex: clear button, or initial value)

type TInputState = {
  origin: TInputOrigin
  text: string
}

const AutocompleteInput = <
  Option extends TOptionTArg,
  FreeSolo extends boolean = false,
>({
  options = [],
  optionsLoading = false,
  freeSolo = false as FreeSolo,
  loading,
  onChange,
  onValidationError,
  onBlur,
  onInputChange,
  input: inputFromProps,
  selectedOption,
  label,
  disabled = false,
  getOptionLabel = it => String(it),
  matchInput: matchInputFromProps,
  renderOption,
  getOptionDisabled,
  error,
  touched,
  required = false,
  placeholder,
  className,
  optionEq,
  ...props
}: TAutocompleteInputProps<Option, FreeSolo>): JSX.Element => {
  const t = useTranslate()
  const [inputState, setInputState] = useImmer<TInputState>({
    text: inputFromProps,
    origin: 'programmatic',
  })

  useEffect(() => {
    setInputState(prev => {
      if (prev.text === inputFromProps) return prev

      return {
        origin: 'programmatic',
        text: inputFromProps,
      }
    })
  }, [inputFromProps, setInputState])

  const matchInput: TMatchInputCb<Option> = useCallback(
    (input, options) => {
      if (typeof matchInputFromProps === 'function')
        return matchInputFromProps(input, options)

      if (input.trim() === '') return { _tag: 'empty' }

      const matches = options.filter(it => {
        return input === getOptionLabel(it)
      })

      if (matches.length === 1) return { _tag: 'match', option: matches[0] }

      if (matches.length > 1)
        return { _tag: 'multiple-matches', options: matches }

      return { _tag: 'no-match', input }
    },
    [getOptionLabel, matchInputFromProps],
  )

  const inputMatch = useMemo(
    () => matchInput(inputState.text, options),
    [inputState.text, matchInput, options],
  )

  const internalError = useMemo((): string | undefined => {
    if (inputState.origin !== 'user') return

    switch (inputMatch._tag) {
      case 'match':
        return
      case 'no-match':
        return 'validation.autocomplete.noMatch'
      case 'multiple-matches':
        return 'validation.autocomplete.multipleMatches'
      case 'empty':
        return // Handled by validator higher up
      default:
        return assertUnreachable(inputMatch)
    }
  }, [inputMatch, inputState.origin])

  const externalError = useMemo((): string | undefined => {
    if (error instanceof Error) return error.message
    if (typeof error === 'string') return error
  }, [error])

  const combinedError = externalError ?? internalError

  useEffect(() => {
    onValidationError?.(internalError)
  }, [internalError, onValidationError])

  return (
    <Autocomplete
      {...props}
      onBlur={event => {
        // Commit typed text that uniquely matches an option: the field shows
        // it without complaint, so leaving the value uncommitted silently
        // submits null
        if (
          !freeSolo &&
          inputState.origin === 'user' &&
          inputMatch._tag === 'match' &&
          getOptionDisabled?.(inputMatch.option) !== true
        ) {
          const option = inputMatch.option
          setInputState({
            text: getOptionLabel(option),
            origin: 'selection',
          })
          onChange(option as Parameters<typeof onChange>[0])
        }
        onBlur?.(event)
      }}
      className={className}
      options={options}
      freeSolo={freeSolo}
      onChange={(_event, value) => {
        let selectedValue: string | Option | null
        let newText: string
        if (typeof value === 'string') {
          const matchedOption = matchInput(value, options).option ?? null
          if (!freeSolo) selectedValue = matchedOption
          else selectedValue = value
          newText = value
        } else {
          selectedValue = value
          newText = value !== null ? getOptionLabel(value) : ''
        }

        setInputState({
          text: newText,
          origin: typeof value === 'string' ? 'user' : 'selection',
        })

        onChange(selectedValue as Parameters<typeof onChange>[0])
      }}
      onInputChange={(_event, value, reason) => {
        setInputState({
          text: value,
          origin: reason === 'reset' ? 'selection' : 'user',
        })

        if (loading) return
        if (reason === 'reset' && value.trim() === '') return

        onInputChange(value ?? '')
      }}
      clearOnBlur={false}
      openOnFocus
      loading={loading}
      disabled={disabled || loading}
      fullWidth
      loadingText={t('global.loading')}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      getOptionDisabled={getOptionDisabled}
      inputValue={inputState.text ?? ''}
      getOptionSelected={optionEq}
      value={selectedOption ?? null}
      placeholder={placeholder}
      id='disable-close-on-select'
      renderInput={params => (
        <TextField
          {...params}
          disabled={disabled || loading}
          label={label}
          helperText={
            combinedError !== undefined ? t(combinedError) : undefined
          }
          error={touched === true && combinedError !== undefined}
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {(loading || optionsLoading) && (
                  <CircularProgress
                    color='inherit'
                    size={20}
                  />
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}

export default AutocompleteInput
